import { useState } from 'react';

export interface ConfirmationDialogConfig {
	open: boolean;
	title: string;
	/** Optional icon rendered beside the title in ConfirmationDialog. */
	titleIcon?: React.ReactNode;
	message?: string;
	content?: React.ReactNode;
	confirmLabel?: string;
	cancelLabel?: string;
	confirmColor?: 'primary' | 'secondary' | 'error' | 'warning';
	/** When true, clicking outside or pressing ESC will not close the dialog. */
	disableBackdropClose?: boolean;
	onConfirm: () => void | Promise<void>;
	onCancel?: () => void;
	loading?: boolean;
	error?: Error | null;
}

export const useConfirmationDialog = () => {
	const [dialogConfig, setDialogConfig] = useState<ConfirmationDialogConfig>({
		open: false,
		title: '',
		message: '',
		confirmLabel: 'Confirm',
		cancelLabel: 'Cancel',
		confirmColor: 'error',
		onConfirm: () => {},
		onCancel: () => {},
		loading: false,
		error: null,
	});

	const openDialog = (config: Omit<ConfirmationDialogConfig, 'open' | 'loading' | 'error'>) => {
		setDialogConfig((prev) => ({
			...config,
			open: true,
			loading: false,
			error: null,
		}));
	};

	const closeDialog = () => {
		setDialogConfig((prev) => ({
			...prev,
			open: false,
			loading: false,
			error: null,
		}));
	};

	const handleConfirm = async () => {
		// Show loading spinner immediately to prevent multiple clicks
		setDialogConfig((prev) => ({ ...prev, loading: true, error: null }));

		// Capture current config to avoid stale closure issues
		const { onConfirm } = dialogConfig;

		try {
			await onConfirm();
			// Close dialog after successful completion
			closeDialog();
		} catch (error) {
			console.error('Confirmation action failed:', error);
			// Keep dialog open and show error state
			setDialogConfig((prev) => ({
				...prev,
				loading: false,
				error: error instanceof Error ? error : new Error(String(error)),
			}));
		}
	};

	const handleCancel = () => {
		// Capture current config to avoid stale closure issues
		const { onCancel } = dialogConfig;

		if (onCancel) {
			onCancel();
		}
		closeDialog();
	};

	return {
		dialogConfig,
		openDialog,
		closeDialog,
		handleConfirm,
		handleCancel,
	};
};
